import { useRouter, useSearchParams } from 'next/navigation';
import React, { useCallback, useState } from 'react';

import SSOButton, { Providers } from '@/components/button/ssoButton/SSOButton';
import Spinner from '@/components/common/loading/spinner';
import Divider from '@/components/divider';
import AcresLogo from '@/components/icons/AcresLogo';
import BaseModal from '@/components/modal/baseModal';
import ModalHeading from '@/components/modal/modalHeading';
import Typography from '@/components/typography';
import { AcresFeatureFlags, AUTH_ACTIONS } from '@/constants/constants';
import { HOME_URL } from '@/constants/constantUrls';
import {
  PLAT_MAP_URL,
  RESET_PASSWORD_URL,
  SIGN_UP_URL,
} from '@/constants/urls';
import { Message } from '@/ory/models/error';
import { LoginMethods } from '@/ory/models/submitMethods/methods';
import { useAnalytics } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/auth-provider';
import { useInitializeContext } from '@/providers/InitializeProvider';
import { useModalContext } from '@/providers/ModalProvider';
import handleOryResponse from '@/utils/ory/handleOryResponse';
import { checkForRedirect } from '@/utils/validRedirect';

import LoginStepOneForm, {
  StepOneFormData,
} from './components/loginStepOneForm';
import LoginStepTwoForm, {
  StepTwoFormData,
} from './components/loginStepTwoForm';

const dontHaveAnAccountText = "Don't have an account?";
const loginFormId = 'login-form';

export type SsoOidcClients = {
  [key: string]: {
    provider: string;
    org_id: string | number;
  };
};

type Props = {
  onCloseModal?: Function;
  onCreateAccountClick?: Function;
  redirect?: boolean;
  isDisabled?: boolean;
};

const LoginModal: React.FC<Props> = ({
  onCloseModal = undefined,
  onCreateAccountClick = undefined,
  redirect = false,
  isDisabled = false,
}) => {
  const router = useRouter();
  const query = useSearchParams();

  const { oryLogin } = useAuth();
  const { setModalContent } = useModalContext();
  const [error, setError] = useState<Message[]>([]);
  const [email, setEmail] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { identify } = useAnalytics();

  const { getFeature } = useInitializeContext();

  // Object type. Key is the email domain, value is an object with OIDC provider and organization id
  // Example: { 'example.com': { provider: 'example', org_id: '123' } }
  const ssoOidcClients = getFeature(
    AcresFeatureFlags.SSO_OIDC_CLIENTS
  ) as SsoOidcClients;

  const from = checkForRedirect(router, (query?.get('from') as string) || '');

  const closeModal = useCallback(() => {
    if (redirect) {
      if (from && typeof from === 'string' && !from.includes('login')) {
        window.location.href = HOME_URL;
      } else {
        router.push(PLAT_MAP_URL);
      }
      return;
    }

    if (onCloseModal) {
      onCloseModal();
    } else {
      setModalContent(null);
    }
  }, [from, onCloseModal, redirect, router, setModalContent]);

  const goToCreateAccount = useCallback(() => {
    if (onCreateAccountClick) {
      onCreateAccountClick();
    } else {
      router.push(`${SIGN_UP_URL}?from=${from}`);
    }
  }, [from, onCreateAccountClick, router]);

  const goToForgotPassword = useCallback(() => {
    setModalContent(null);
    router.push(RESET_PASSWORD_URL);
  }, [router, setModalContent]);

  const handleOnSuccess = useCallback(() => {
    if (redirect) {
      if (from && !from.includes('login')) {
        window.location.href = from;
      } else {
        router.push(PLAT_MAP_URL);
      }
    } else {
      closeModal();
    }
  }, [closeModal, from, redirect, router]);

  const handleContinueClick = useCallback(
    (data: StepOneFormData) => {
      const { email: stepOneEmail } = data;

      if (stepOneEmail === '') return;
      if (stepOneEmail.split('@').length !== 2) return;
      const emailDomain = stepOneEmail.split('@')[1].toLowerCase();

      // Domain is in the list of SSO OIDC clients so we should redirect to the SSO login
      if (ssoOidcClients?.[emailDomain]) {
        oryLogin({
          method: LoginMethods.OIDC,
          provider: ssoOidcClients[emailDomain].provider,
        }).then((res) => {
          handleOryResponse(res, {
            onSuccess: () => {},
            setError,
            setIsLoading,
          });
        });
        return;
      }

      setEmail(stepOneEmail);
    },
    [oryLogin, ssoOidcClients]
  );

  const onSubmit = useCallback(
    async (data: StepTwoFormData) => {
      identify({ email: data?.email });

      oryLogin({
        identifier: data.email,
        method: LoginMethods.PASSWORD,
        password: data.password,
      }).then((res) => {
        handleOryResponse(res, {
          onSuccess: handleOnSuccess,
          setError,
          setIsLoading,
        });
      });
    },
    [handleOnSuccess, identify, oryLogin]
  );

  if (isDisabled) {
    return (
      <BaseModal
        Content={
          <div className="-mt-2 flex min-h-[440px] flex-col items-center justify-center">
            <Spinner />
          </div>
        }
        className="w-[560px] shadow-elevation-1"
      />
    );
  }

  return (
    <BaseModal
      Content={
        <div className="-mt-2 flex flex-col">
          {/* Login forms */}
          {!email ? (
            <LoginStepOneForm
              formId={loginFormId}
              handleContinue={handleContinueClick}
              isLoading={isLoading}
            />
          ) : (
            <LoginStepTwoForm
              email={email}
              formId={loginFormId}
              goToForgotPassword={goToForgotPassword}
              handleOnSubmit={onSubmit}
              isLoading={isLoading}
              setEmail={setEmail}
            />
          )}

          {/* Or divider */}
          <div className="my-6 flex items-center gap-x-4">
            <Divider className="flex-grow" />
            <Typography className="text-gray-50" variant="body2">
              or
            </Typography>
            <Divider className="flex-grow" />
          </div>

          <div className="flex flex-col items-center justify-center gap-y-4">
            <SSOButton
              canSubmit={() => true}
              location="login"
              provider={Providers.GOOGLE}
              setError={setError}
              setIsLoading={setIsLoading}
            />
            <SSOButton
              canSubmit={() => true}
              location="login"
              provider={Providers.APPLE}
              setError={setError}
              setIsLoading={setIsLoading}
            />
          </div>
        </div>
      }
      Footer={
        <div className="flex flex-col items-center gap-x-1 gap-y-4 text-center">
          {/* Ory errors */}
          {error.length > 0 &&
            error.map((e) => (
              <Typography
                key={e.id}
                className="text-red-utility"
                variant="body3"
              >
                {e.text}
              </Typography>
            ))}

          {/* Already have an account? */}
          <div className="flex items-center gap-x-1 text-center">
            <Typography className="text-gray-70" variant="body2">
              {dontHaveAnAccountText}
            </Typography>

            <button type="button" onClick={goToCreateAccount}>
              <Typography className="text-green-50 underline" variant="body2">
                {AUTH_ACTIONS.CREATE_ACCOUNT}
              </Typography>
            </button>
          </div>
        </div>
      }
      Header={
        <ModalHeading
          className="flex flex-col items-center gap-x-1 gap-y-4 text-center"
          icon={<AcresLogo className="h-10" />}
          title=""
          onXClick={closeModal}
        />
      }
      SupportText={undefined}
      className="w-[560px] shadow-elevation-1"
    />
  );
};

export default LoginModal;
